import { BRAND_KIT_FIELDS, type BrandKitField, type BrandKitValues } from '@castfeeds/schema';

/** A kit row as stored: customer kits hold every field; project kits hold only their overrides. */
export interface StoredKit {
  id: string;
  parentId: string | null;
  owner: 'customer' | 'project';
  values: Partial<BrandKitValues>;
  overrides: BrandKitField[];
}

export type FieldSource = 'own' | 'inherited';

export interface EffectiveKit {
  values: Partial<BrandKitValues>;
  sources: Record<BrandKitField, FieldSource>;
}

/**
 * Effective kit = parent fields + the child's overrides (tech spec §Brand kits).
 * A customer kit (no parent) owns every field it has.
 */
export function resolveEffectiveKit(kit: StoredKit, parent: StoredKit | null): EffectiveKit {
  const values: Partial<BrandKitValues> = {};
  const sources = {} as Record<BrandKitField, FieldSource>;
  for (const field of BRAND_KIT_FIELDS) {
    const own = !parent || kit.overrides.includes(field);
    const src = own ? kit.values : parent.values;
    if (src[field] !== undefined) (values as Record<string, unknown>)[field] = src[field];
    sources[field] = own ? 'own' : 'inherited';
  }
  return { values, sources };
}

/** Set a field on a kit. On a project kit this records an override. */
export function setKitField<F extends BrandKitField>(kit: StoredKit, field: F, value: BrandKitValues[F]): StoredKit {
  const overrides = kit.owner === 'project' && !kit.overrides.includes(field) ? [...kit.overrides, field] : kit.overrides;
  return { ...kit, values: { ...kit.values, [field]: value }, overrides };
}

/** "Reset" on a project kit: drop the override so the field inherits again. */
export function resetKitField(kit: StoredKit, field: BrandKitField): StoredKit {
  if (kit.owner !== 'project') return kit;
  const values = { ...kit.values };
  delete values[field];
  return { ...kit, values, overrides: kit.overrides.filter((f) => f !== field) };
}

/** Pick black or white text for a background colour (same luminance rule as the prototype). */
export function textOn(hex: string): '#121318' | '#FFFFFF' {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 > 150 ? '#121318' : '#FFFFFF';
}

/** WCAG 2.x relative-luminance contrast ratio between two hex colours. */
export function contrastRatio(a: string, b: string): number {
  const lum = (hex: string) => {
    const h = hex.replace('#', '');
    const [r, g, bl] = [0, 2, 4].map((i) => {
      const c = parseInt(h.slice(i, i + 2), 16) / 255;
      return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
    });
    return 0.2126 * r + 0.7152 * g + 0.0722 * bl;
  };
  const [hi, lo] = [lum(a), lum(b)].sort((x, y) => y - x);
  return Math.round(((hi + 0.05) / (lo + 0.05)) * 100) / 100;
}

export const DEFAULT_KIT_VALUES: BrandKitValues = {
  colours: { primary: '#FF4A2E', ink: '#121318', paper: '#F6F4EF', secondary: '#2F5BFF', highlight: '#FFD23F' },
  heading_font: 'Rubik',
  body_font: 'Heebo',
  captions: { preset: 'bold_pop', words_per_line: 3, position: 'low' },
  lower_third: { style: 'bar' },
  music_mood: 'upbeat',
  logo_bug: { corner: 'top_right' },
  logos: {},
  end_card: { text: '', duration_ms: 2000 },
  disclaimer: '',
};
